import pagesConfig from '@/pages.json'
import { isMpWeixin } from './platform'
import { getENV } from '@/utils/env'

const { pages, subPackages, tabBar = { list: [] } } = { ...pagesConfig }

export const getLastPage = () => {
  // getCurrentPages() 至少有1个元素，所以不再额外判断
  // const lastPage = getCurrentPages().at(-1)
  // 上面那个在低版本安卓中打包会报错，所以改用下面这个【虽然我加了 src/interceptions/prototype.ts，但依然报错】
  const pages = getCurrentPages()
  return pages[pages.length - 1]
}

export const tabBarList = tabBar?.list || []

/** 判断当前页面是否是 tabbar 页  */
export const getIsTabbar = () => {
  try {
    const lastPage = getLastPage()
    const currPath = lastPage?.route

    return Boolean(tabBar?.list?.some((item) => item.pagePath === currPath))
  } catch {
    return false
  }
}

/**
 * 判断指定页面是否是 tabbar 页
 * @param path 页面路径
 * @returns true: 是 tabbar 页 false: 不是 tabbar 页
 */
export const isTableBar = (path: string) => {
  if (!tabBar) {
    return false
  }
  if (!tabBar.list.length) {
    // 通常有 tabBar 的话，list 不能有空，且至少有2个元素，这里其实不用处理
    return false
  }
  // 这里需要处理一下 path，因为 tabBar 中的 pagePath 是不带 /pages 前缀的
  if (path.startsWith('/')) {
    path = path.substring(1)
  }
  return !!tabBar.list.find((e) => e.pagePath === path)
}

/**
 * 获取当前页面路由的 path 路径和 redirectPath 路径
 * path 如 '/pages/login/index'
 * redirectPath 如 '/pages/demo/base/route-interceptor'
 */
export const currRoute = () => {
  const lastPage = getLastPage()
  const currRoute = (lastPage as any).$page
  // console.log('lastPage.$page:', currRoute)
  // console.log('lastPage.$page.fullpath:', currRoute.fullPath)
  // console.log('lastPage.$page.options:', currRoute.options)
  // console.log('lastPage.options:', (lastPage as any).options)
  // 经过多端测试，只有 fullPath 靠谱，其他都不靠谱
  const { fullPath } = currRoute as { fullPath: string }
  // console.log(fullPath)
  // eg: /pages/login/index?redirect=%2Fpages%2Fdemo%2Fbase%2Froute-interceptor (小程序)
  // eg: /pages/login/index?redirect=%2Fpages%2Froute-interceptor%2Findex%3Fname%3Dfeige%26age%3D30(h5)
  return getUrlObj(fullPath)
}

const ensureDecodeURIComponent = (url: string) => {
  if (url.startsWith('%')) {
    return ensureDecodeURIComponent(decodeURIComponent(url))
  }
  return url
}
/**
 * 解析 url 得到 path 和 query
 * 比如输入url: /pages/login/index?redirect=%2Fpages%2Fdemo%2Fbase%2Froute-interceptor
 * 输出: {path: /pages/login/index, query: {redirect: /pages/demo/base/route-interceptor}}
 */
export const getUrlObj = (url: string) => {
  const [path, queryStr] = url.split('?')
  // console.log(path, queryStr)

  if (!queryStr) {
    return {
      path,
      query: {},
    }
  }
  const query: Record<string, string> = {}
  queryStr.split('&').forEach((item) => {
    const [key, value] = item.split('=')
    // console.log(key, value)
    query[key] = ensureDecodeURIComponent(value) // 这里需要统一 decodeURIComponent 一下，可以兼容h5和微信y
  })
  return { path, query }
}
/**
 * 得到所有的需要登录的 pages，包括主包和分包的
 * 这里设计得通用一点，可以传递 key 作为判断依据，默认是 needLogin, 与 route-block 配对使用
 * 如果没有传 key，则表示所有的 pages，如果传递了 key, 则表示通过 key 过滤
 */
export const getAllPages = (key = 'needLogin') => {
  // 这里处理主包
  const mainPages = [
    ...pages
      .filter((page) => !key || page[key])
      .map((page) => ({
        ...page,
        path: `/${page.path}`,
      })),
  ]
  // 这里处理分包
  const subPages: any[] = []
  subPackages.forEach((subPageObj) => {
    // console.log(subPageObj)
    const { root } = subPageObj

    subPageObj.pages
      .filter((page) => !key || page[key])
      .forEach((page: { path: string } & Record<string, any>) => {
        subPages.push({
          ...page,
          path: `/${root}/${page.path}`,
        })
      })
  })
  const result = [...mainPages, ...subPages]
  // console.log(`getAllPages by ${key} result: `, result)
  return result
}

/**
 * 得到所有的需要登录的 pages，包括主包和分包的
 * 只得到 path 数组
 */
export const getNeedLoginPages = (): string[] => getAllPages('needLogin').map((page) => page.path)

/**
 * 得到所有的需要登录的 pages，包括主包和分包的
 * 只得到 path 数组
 */
export const needLoginPages: string[] = getAllPages('needLogin').map((page) => page.path)

/**
 * 根据微信小程序当前环境，判断应该获取的 baseUrl
 */
export const getEnvBaseUrl = () => {
  // 请求基准地址
  let baseUrl = ''

  // 微信小程序端环境区分
  if (isMpWeixin) {
    const {
      miniProgram: { envVersion },
    } = uni.getAccountInfoSync()

    const { dev, trial, release } = getENV()

    switch (envVersion) {
      case 'develop':
        baseUrl = dev
        break
      case 'trial':
        baseUrl = trial
        break
      case 'release':
        baseUrl = release
        break
    }
  } else {
    // H5 或其他非小程序环境
    // 优先从环境变量获取 VITE_APP_BASE_URL，如果没有则使用默认值或报错
    // 注意：确保在 .env 文件中定义了 VITE_APP_BASE_URL
    console.log('[ import.meta.env ] >', import.meta.env)
    const envUrl = import.meta.env.VITE_APP_BASE_URL
    if (envUrl) {
      baseUrl = envUrl
    } else {
      // 默认 fallback，或者根据 process.env.NODE_ENV 进一步区分
      // 这里假设开发环境默认使用测试地址，生产环境应强制配置环境变量
      console.warn('未配置 VITE_APP_BASE_URL，使用默认测试地址')
      baseUrl = 'https://shop-hxbank.yqtbuy.com'
    }
  }

  return baseUrl
}

interface DateCheckResult {
  isExpired: boolean // 是否已过期
  isNearExpiry: boolean // 是否临近过期
  isLongTerm?: boolean // 长期有效
  daysRemaining: number // 剩余天数
  message: string // 状态消息
  level: 'normal' | 'warning' | 'danger' | 'expired' // 严重级别
}

/**
 * 检查日期是否临近失效
 * @param expiryDate 失效日期，格式为 YYYY-MM-DD
 * @returns 返回检查结果对象
 */
export const checkExpiryStatus = (expiryDate: string, time?: string): DateCheckResult => {
  if (expiryDate === '') {
    return {
      isExpired: false,
      isNearExpiry: false,
      daysRemaining: 0,
      message: '未过期',
      level: 'normal',
    }
  }
  // 当前日期
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  // 解析失效日期
  const [year, month, day] = expiryDate.split('-').map(Number)
  const expiry = new Date(year, month - 1, day)
  expiry.setHours(0, 0, 0, 0)
  // 计算剩余天数
  const timeDiff = expiry.getTime() - today.getTime()
  const daysRemaining = Math.ceil(timeDiff / (1000 * 60 * 60 * 24))

  // 长期有效
  if (year === 2099) {
    return {
      isExpired: false,
      isNearExpiry: false,
      isLongTerm: true,
      daysRemaining: 0,
      message: '长期有效',
      level: 'normal',
    }
  }

  // 已过期
  if (daysRemaining < 0) {
    return {
      isExpired: true,
      isNearExpiry: false,
      daysRemaining: 0,
      message: '已过期',
      level: 'expired',
    }
  }

  // 不足1天
  if (daysRemaining === 0) {
    return {
      isExpired: false,
      isNearExpiry: true,
      daysRemaining: 0,
      message: `${time} 过期`,
      level: 'danger',
    }
  }

  // 不足7天
  if (daysRemaining <= 7) {
    return {
      isExpired: false,
      isNearExpiry: true,
      daysRemaining,
      message: `剩余${daysRemaining}天`,
      level: 'danger',
    }
  }

  // 不足30天
  if (daysRemaining <= 30) {
    return {
      isExpired: false,
      isNearExpiry: true,
      daysRemaining,
      message: `剩余${daysRemaining}天`,
      level: 'warning',
    }
  }

  // 正常情况
  return {
    isExpired: false,
    isNearExpiry: false,
    daysRemaining,
    message: `剩余${daysRemaining}天`,
    level: 'normal',
  }
}

/**
 * 将字符串中间部分替换为星号
 * @param {string|number} input - 输入字符串或数字
 * @param {number} [visiblePrefix=3] - 开头保留的可见字符数
 * @param {number} [visibleSuffix=4] - 结尾保留的可见字符数
 * @returns {string} 加密后的字符串
 */
export const toAsteriskText = (input, visiblePrefix = 3, visibleSuffix = 4) => {
  const str = String(input)
  if (str.length <= visiblePrefix + visibleSuffix) {
    return str // 太短则不处理
  }

  const prefix = str.substring(0, visiblePrefix)
  const suffix = str.substring(str.length - visibleSuffix)
  const asterisk = '*'.repeat(str.length - visiblePrefix - visibleSuffix)

  return prefix + asterisk.substring(0, 5) + suffix
}

// 在页面方法或生命周期钩子中
export const getCurrentRouteInfo = () => {
  // 获取页面栈和当前页实例
  const pages = getCurrentPages()
  if (!pages.length) {
    console.log('当前没有页面栈')
    return
  }
  const currentPage = pages[pages.length - 1]

  // 1. 获取基本路由路径
  const routePath = currentPage.route // 例如: "pages/index/index"
  console.log('当前页面路径:', routePath)

  // 2. 获取URL参数对象
  const queryParams = currentPage.options || {}
  console.log('URL参数:', queryParams)
  // 获取单个参数，例如name
  if (queryParams.name) {
    console.log('name参数:', queryParams.name)
  }

  // 3. (非标准方法，需注意兼容性) 尝试获取完整路径
  let fullPath = ''
  if (currentPage.$page && currentPage.$page.fullPath) {
    fullPath = currentPage.$page.fullPath
  } else if (currentPage.__displayReporter__ && currentPage.__displayReporter__.path) {
    fullPath = currentPage.__displayReporter__.path
  }
  console.log('完整路径:', fullPath)

  // 返回需要的信息，或进行其他操作
  return {
    routePath,
    queryParams,
    fullPath,
  }
}
